import React, { forwardRef } from 'react';
import { 
  AlertCircle, 
  Eye, 
  EyeOff, 
  Search, 
  Calendar, 
  Phone, 
  Mail, 
  Lock,
  ChevronDown,
  Upload,
  X,
  File,
  Plus,
  Minus,
} from 'lucide-react';

// ========================================
// INPUT COMPONENT
// ========================================
export const Input = forwardRef(({
  // Basic Props
  type = 'text',
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  disabled = false,
  readOnly = false,
  autoFocus = false,
  
  // Label & Helper Text
  label,
  helperText,
  required = false,
  optional = false,
  
  // Validation
  error,
  success,
  
  // Icons
  icon,
  iconPosition = 'left', // 'left' or 'right'
  
  // Special Features
  showPasswordToggle = false,
  clearable = false,
  maxLength,
  
  // Styling
  className = '',
  inputClassName = '',
  fullWidth = true,
  size = 'md', // 'sm', 'md', 'lg'
  
  ...props
}, ref) => {
  const [showPassword, setShowPassword] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-3 py-2 text-sm',
    lg: 'px-4 py-3 text-base',
  };

  const getIcon = () => {
    if (!icon) return null;
    
    const iconMap = {
      search: Search,
      calendar: Calendar,
      phone: Phone,
      mail: Mail,
      lock: Lock,
    };
    
    const IconComponent = typeof icon === 'string' ? iconMap[icon] : icon;
    return IconComponent ? <IconComponent className="w-4 h-4 text-gray-400" /> : null;
  };

  const handleClear = () => {
    if (onChange) {
      onChange({ target: { name, value: '' } });
    }
  };

  const inputType = type === 'password' && showPassword ? 'text' : type;

  return (
    <div className={`${fullWidth ? 'w-full' : ''} ${className}`}>
      {label && (
        <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
          {optional && <span className="text-gray-400 text-xs ml-1">(Optional)</span>}
        </label>
      )}

      <div className="relative">
        {icon && iconPosition === 'left' && (
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            {getIcon()}
          </div>
        )}

        <input
          ref={ref}
          type={inputType}
          name={name}
          id={name}
          value={value}
          onChange={onChange}
          onBlur={(e) => {
            setIsFocused(false);
            onBlur && onBlur(e);
          }}
          onFocus={() => setIsFocused(true)}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          autoFocus={autoFocus}
          maxLength={maxLength}
          className={`
            w-full rounded-lg border transition-all duration-200
            ${sizeClasses[size]}
            ${icon && iconPosition === 'left' ? 'pl-10' : ''}
            ${icon && iconPosition === 'right' ? 'pr-10' : ''}
            ${showPasswordToggle || clearable ? 'pr-10' : ''}
            ${error ? 'border-red-500 focus:ring-2 focus:ring-red-500 focus:border-transparent' : ''}
            ${success ? 'border-green-500 focus:ring-2 focus:ring-green-500 focus:border-transparent' : ''}
            ${!error && !success ? 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent' : ''}
            ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'bg-white'}
            ${readOnly ? 'bg-gray-50' : ''}
            ${inputClassName}
          `}
          {...props}
        />

        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
          {type === 'password' && showPasswordToggle && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          )}

          {clearable && value && !disabled && !readOnly && (
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              tabIndex={-1}
            >
              <X className="w-4 h-4" />
            </button>
          )}

          {icon && iconPosition === 'right' && (
            <div className="pointer-events-none">{getIcon()}</div>
          )}
        </div>

        {maxLength && isFocused && (
          <div className="absolute -bottom-5 right-0 text-xs text-gray-400">
            {value?.length || 0} / {maxLength}
          </div>
        )}
      </div>

      {(helperText || error || success) && (
        <div className="mt-1">
          {error && (
            <p className="text-xs text-red-500 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {error}
            </p>
          )}
          {success && !error && (
            <p className="text-xs text-green-500 flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {success}
            </p>
          )}
          {helperText && !error && !success && (
            <p className="text-xs text-gray-500">{helperText}</p>
          )}
        </div>
      )}
    </div>
  );
});

Input.displayName = 'Input';


// ========================================
// TEXTAREA COMPONENT
// ========================================
export const Textarea = forwardRef(({
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  disabled = false,
  readOnly = false,
  rows = 4,
  
  label,
  helperText,
  required = false,
  optional = false,
  
  error,
  success,
  
  maxLength,
  showCount = false,
  autoResize = false,
  
  className = '',
  textareaClassName = '',
  fullWidth = true,
  
  ...props
}, ref) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const textareaRef = React.useRef(null);

  React.useEffect(() => {
    if (autoResize && textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [value, autoResize]);

  return (
    <div className={`${fullWidth ? 'w-full' : ''} ${className}`}>
      {label && (
        <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
          {optional && <span className="text-gray-400 text-xs ml-1">(Optional)</span>}
        </label>
      )}

      <div className="relative">
        <textarea
          ref={ref || textareaRef}
          name={name}
          id={name}
          value={value}
          onChange={onChange}
          onBlur={(e) => {
            setIsFocused(false);
            onBlur && onBlur(e);
          }}
          onFocus={() => setIsFocused(true)}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          rows={autoResize ? 1 : rows}
          maxLength={maxLength}
          className={`
            w-full px-3 py-2 rounded-lg border transition-all duration-200 resize-none
            ${error ? 'border-red-500 focus:ring-2 focus:ring-red-500 focus:border-transparent' : ''}
            ${success ? 'border-green-500 focus:ring-2 focus:ring-green-500 focus:border-transparent' : ''}
            ${!error && !success ? 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent' : ''}
            ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'bg-white'}
            ${readOnly ? 'bg-gray-50' : ''}
            ${textareaClassName}
          `}
          {...props}
        />

        {(showCount || maxLength) && (
          <div className="absolute bottom-2 right-2 text-xs text-gray-400 bg-white px-1">
            {value?.length || 0}{maxLength && ` / ${maxLength}`}
          </div>
        )}
      </div>

      {(helperText || error || success) && (
        <div className="mt-1">
          {error && (
            <p className="text-xs text-red-500 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {error}
            </p>
          )}
          {success && !error && (
            <p className="text-xs text-green-500 flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {success}
            </p>
          )}
          {helperText && !error && !success && (
            <p className="text-xs text-gray-500">{helperText}</p>
          )}
        </div>
      )}
    </div>
  );
});

Textarea.displayName = 'Textarea';


// ========================================
// SELECT COMPONENT
// ========================================
export const Select = forwardRef(({
  name,
  value,
  onChange,
  onBlur,
  options = [],
  disabled = false,
  
  label,
  helperText,
  required = false,
  optional = false,
  placeholder = 'Select an option',
  
  error,
  success,
  
  className = '',
  selectClassName = '',
  fullWidth = true,
  size = 'md',
  
  ...props
}, ref) => {
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-3 py-2 text-sm',
    lg: 'px-4 py-3 text-base',
  };

  return (
    <div className={`${fullWidth ? 'w-full' : ''} ${className}`}>
      {label && (
        <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
          {optional && <span className="text-gray-400 text-xs ml-1">(Optional)</span>}
        </label>
      )}

      <div className="relative">
        <select
          ref={ref}
          name={name}
          id={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          className={`
            w-full rounded-lg border transition-all duration-200 appearance-none
            ${sizeClasses[size]}
            pr-10
            ${error ? 'border-red-500 focus:ring-2 focus:ring-red-500 focus:border-transparent' : ''}
            ${success ? 'border-green-500 focus:ring-2 focus:ring-green-500 focus:border-transparent' : ''}
            ${!error && !success ? 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent' : ''}
            ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'bg-white'}
            ${selectClassName}
          `}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option, index) => (
            <option 
              key={index} 
              value={typeof option === 'object' ? option.value : option}
              disabled={option.disabled}
            >
              {typeof option === 'object' ? option.label : option}
            </option>
          ))}
        </select>

        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <ChevronDown className="w-4 h-4 text-gray-400" />
        </div>
      </div>

      {(helperText || error || success) && (
        <div className="mt-1">
          {error && (
            <p className="text-xs text-red-500 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {error}
            </p>
          )}
          {success && !error && (
            <p className="text-xs text-green-500 flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {success}
            </p>
          )}
          {helperText && !error && !success && (
            <p className="text-xs text-gray-500">{helperText}</p>
          )}
        </div>
      )}
    </div>
  );
});

Select.displayName = 'Select';


// ========================================
// CHECKBOX COMPONENT
// ========================================
export const Checkbox = forwardRef(({
  name,
  checked,
  onChange,
  disabled = false,
  
  label,
  description,
  
  className = '',
  size = 'md',
  
  ...props
}, ref) => {
  const sizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  return (
    <div className={`flex items-start ${className}`}>
      <div className="flex items-center h-5">
        <input
          ref={ref}
          type="checkbox"
          name={name}
          id={name}
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className={`
            ${sizeClasses[size]}
            text-blue-600 rounded
            border-gray-300
            focus:ring-2 focus:ring-blue-500 focus:ring-offset-0
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            transition-colors
          `}
          {...props}
        />
      </div>
      {(label || description) && (
        <div className="ml-2">
          {label && (
            <label 
              htmlFor={name} 
              className={`
                block font-medium text-gray-700
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                ${size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-base' : 'text-sm'}
              `}
            >
              {label}
            </label>
          )}
          {description && (
            <p className={`text-gray-500 ${size === 'sm' ? 'text-xs' : 'text-sm'}`}>
              {description}
            </p>
          )}
        </div>
      )}
    </div>
  );
});

Checkbox.displayName = 'Checkbox';


// ========================================
// RADIO COMPONENT
// ========================================
export const Radio = forwardRef(({
  name,
  value,
  checked,
  onChange,
  disabled = false,
  
  label,
  description,
  
  className = '',
  size = 'md',
  
  ...props
}, ref) => {
  const sizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  return (
    <div className={`flex items-start ${className}`}>
      <div className="flex items-center h-5">
        <input
          ref={ref}
          type="radio"
          name={name}
          id={`${name}-${value}`}
          value={value}
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className={`
            ${sizeClasses[size]}
            text-blue-600
            border-gray-300
            focus:ring-2 focus:ring-blue-500 focus:ring-offset-0
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            transition-colors
          `}
          {...props}
        />
      </div>
      {(label || description) && (
        <div className="ml-2">
          {label && (
            <label 
              htmlFor={`${name}-${value}`}
              className={`
                block font-medium text-gray-700
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                ${size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-base' : 'text-sm'}
              `}
            >
              {label}
            </label>
          )}
          {description && (
            <p className={`text-gray-500 ${size === 'sm' ? 'text-xs' : 'text-sm'}`}>
              {description}
            </p>
          )}
        </div>
      )}
    </div>
  );
});

Radio.displayName = 'Radio';


// ========================================
// FILEUPLOAD COMPONENT
// ========================================
export const FileUpload = forwardRef(({
  name,
  onChange,
  accept,
  multiple = false,
  disabled = false,
  maxSize = 5, // MB
  
  label,
  helperText,
  required = false,
  
  error,
  
  value,
  onRemove,
  
  className = '',
  
  ...props
}, ref) => {
  const [dragActive, setDragActive] = React.useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (files) => {
    const fileArray = Array.from(files);
    
    const oversized = fileArray.filter(file => file.size > maxSize * 1024 * 1024);
    if (oversized.length > 0) {
      alert(`File size should not exceed ${maxSize}MB`);
      return;
    }
    
    onChange && onChange({ target: { name, files: multiple ? fileArray : fileArray[0] } });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const files = multiple ? (value || []) : (value ? [value] : []);

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center transition-colors
          ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}
          ${error ? 'border-red-500 bg-red-50' : ''}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-blue-500 hover:bg-blue-50'}
        `}
      >
        <input
          ref={ref}
          type="file"
          name={name}
          onChange={handleChange}
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          className="hidden"
          id={name}
          {...props}
        />
        
        <label htmlFor={name} className="cursor-pointer">
          <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm font-medium text-gray-700 mb-1">
            {dragActive ? 'Drop files here' : 'Click to upload or drag and drop'}
          </p>
          <p className="text-xs text-gray-500">
            {accept || 'Any file'} • Max {maxSize}MB
          </p>
        </label>
      </div>

      {files.length > 0 && (
        <div className="mt-3 space-y-2">
          {files.map((file, index) => (
            <div 
              key={index}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <File className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {file.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatFileSize(file.size)}
                  </p>
                </div>
              </div>
              {onRemove && (
                <button
                  type="button"
                  onClick={() => onRemove(index)}
                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                >
                  <X className="w-4 h-4 text-gray-600" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {(helperText || error) && (
        <div className="mt-1">
          {error && (
            <p className="text-xs text-red-500 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {error}
            </p>
          )}
          {helperText && !error && (
            <p className="text-xs text-gray-500">{helperText}</p>
          )}
        </div>
      )}
    </div>
  );
});

FileUpload.displayName = 'FileUpload';


// ========================================
// NUMBER INPUT WITH STEPPER
// ========================================
export const NumberInput = forwardRef(({
  name,
  value,
  onChange,
  min,
  max,
  step = 1,
  disabled = false,
  
  label,
  helperText,
  required = false,
  error,
  
  className = '',
  showStepper = true,
  
  ...props
}, ref) => {
  const increment = () => {
    const newValue = parseFloat(value || 0) + step;
    if (max === undefined || newValue <= max) {
      onChange({ target: { name, value: newValue } });
    }
  };

  const decrement = () => {
    const newValue = parseFloat(value || 0) - step;
    if (min === undefined || newValue >= min) {
      onChange({ target: { name, value: newValue } });
    }
  };

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        <input
          ref={ref}
          type="number"
          name={name}
          id={name}
          value={value}
          onChange={onChange}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          className={`
            w-full px-3 py-2 rounded-lg border transition-all duration-200
            ${showStepper ? 'pr-20' : ''}
            ${error ? 'border-red-500 focus:ring-2 focus:ring-red-500 focus:border-transparent' : 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent'}
            ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'bg-white'}
          `}
          {...props}
        />

        {showStepper && (
          <div className="absolute right-1 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
            <button
              type="button"
              onClick={decrement}
              disabled={disabled || (min !== undefined && parseFloat(value || 0) <= min)}
              className="p-1 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Minus className="w-4 h-4 text-gray-600" />
            </button>
            <button
              type="button"
              onClick={increment}
              disabled={disabled || (max !== undefined && parseFloat(value || 0) >= max)}
              className="p-1 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        )}
      </div>

      {(helperText || error) && (
        <div className="mt-1">
          {error && (
            <p className="text-xs text-red-500 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {error}
            </p>
          )}
          {helperText && !error && (
            <p className="text-xs text-gray-500">{helperText}</p>
          )}
        </div>
      )}
    </div>
  );
});

NumberInput.displayName = 'NumberInput';


// ========================================
// SWITCH / TOGGLE COMPONENT
// ========================================
export const Switch = forwardRef(({
  name,
  checked,
  onChange,
  disabled = false,
  
  label,
  description,
  
  className = '',
  
  ...props
}, ref) => {
  return (
    <div className={`flex items-center justify-between ${className}`}>
      <div className="flex-1">
        {label && (
          <label 
            htmlFor={name}
            className={`block text-sm font-medium text-gray-700 ${disabled ? 'opacity-50' : 'cursor-pointer'}`}
          >
            {label}
          </label>
        )}
        {description && (
          <p className="text-xs text-gray-500 mt-1">{description}</p>
        )}
      </div>
      
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={checked}
        name={name}
        id={name}
        onClick={() => !disabled && onChange({ target: { name, checked: !checked } })}
        disabled={disabled}
        className={`
          relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
          ${checked ? 'bg-blue-600' : 'bg-gray-200'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
        {...props}
      >
        <span
          className={`
            pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out
            ${checked ? 'translate-x-5' : 'translate-x-0'}
          `}
        />
      </button>
    </div>
  );
});

Switch.displayName = 'Switch';


// ========================================
// EXPORT ALL COMPONENTS
// ========================================
export default {
  Input,
  Textarea,
  Select,
  Checkbox,
  Radio,
  FileUpload,
  NumberInput,
  Switch,
};
